// Context-fill signal for the watchdog.
//
// Primary source: the statusline bridge file (/tmp/claude-ctx-<session>.json)
// written by the agent's statusline hook. Fallback: a transcript-size heuristic.
// The bridge file is exact; the heuristic is explicitly marked an estimate.

import { existsSync, readFileSync, statSync, readdirSync } from "node:fs";
import { join } from "node:path";

export interface ContextSignal {
  /** Context fill as a percentage (0–100). */
  percent: number;
  /** True when derived from the transcript heuristic rather than the bridge file. */
  estimate: boolean;
  source: "bridge" | "transcript" | "none";
}

/**
 * Bridge file path for a session id. The statusline hook writes to
 * /tmp/claude-ctx-<id>.json (a literal /tmp, not os.tmpdir(), which on macOS is
 * /var/folders/...). `dir` is overridable for tests.
 */
export function bridgePath(sessionId: string, dir = "/tmp"): string {
  return join(dir, `claude-ctx-${sessionId}.json`);
}

/**
 * Find the active session's bridge file without knowing the session id. Scans
 * for `claude-ctx-*.json` written within `maxAgeMs` (a stale file means that
 * session ended — ignored, so the watchdog shows "no live session" rather than a
 * dead reading). When `projectRoot` is given, prefer a bridge whose recorded
 * `cwd` belongs to that project (one path contains the other) so the GUI tracks
 * its own session when several run at once; otherwise the freshest wins.
 */
export function discoverFreshBridge(
  projectRoot?: string,
  dir = "/tmp",
  maxAgeMs = 30 * 60_000,
  now = Date.now()
): string | undefined {
  let best: { path: string; mtime: number; match: boolean } | undefined;
  try {
    for (const f of readdirSync(dir)) {
      if (!f.startsWith("claude-ctx-") || !f.endsWith(".json")) continue;
      const p = join(dir, f);
      try {
        const mtime = statSync(p).mtimeMs;
        if (now - mtime > maxAgeMs) continue; // stale session
        const match = projectRoot ? bridgeMatchesProject(p, projectRoot) : false;
        // Prefer a project match; among equals, the freshest.
        if (!best || (match && !best.match) || (match === best.match && mtime > best.mtime)) {
          best = { path: p, mtime, match };
        }
      } catch {
        /* unreadable — skip */
      }
    }
  } catch {
    /* dir missing — no signal */
  }
  return best?.path;
}

/** True when the bridge file's recorded session cwd belongs to `projectRoot`. */
function bridgeMatchesProject(bridgeFile: string, projectRoot: string): boolean {
  try {
    const raw = JSON.parse(readFileSync(bridgeFile, "utf-8")) as { cwd?: unknown };
    if (typeof raw.cwd !== "string" || !raw.cwd) return false;
    return raw.cwd === projectRoot || raw.cwd.startsWith(projectRoot) || projectRoot.startsWith(raw.cwd);
  } catch {
    return false;
  }
}

/**
 * Read context fill from the bridge file. Returns null if absent/unparseable.
 * The file is written by the statusline hook; we read `remaining_percentage`
 * (what gsd-context-monitor uses) or `percentage`/`used_percentage` if present.
 */
export function readBridgeSignal(path: string): ContextSignal | null {
  if (!existsSync(path)) return null;
  try {
    const raw = JSON.parse(readFileSync(path, "utf-8")) as Record<string, unknown>;
    const used = pickUsedPercent(raw);
    if (used === null) return null;
    return { percent: clamp(used), estimate: false, source: "bridge" };
  } catch {
    return null;
  }
}

/** Map the bridge file's fields to a "used %" regardless of which it provides. */
function pickUsedPercent(raw: Record<string, unknown>): number | null {
  if (typeof raw.used_percentage === "number") return raw.used_percentage;
  if (typeof raw.percentage === "number") return raw.percentage;
  if (typeof raw.remaining_percentage === "number") return 100 - raw.remaining_percentage;
  return null;
}

/**
 * Estimate context fill from transcript size. Coarse: tokens ≈ bytes / 4,
 * against a model window. Always flagged as an estimate.
 */
export function transcriptHeuristic(
  transcriptPath: string,
  windowTokens = 200_000
): ContextSignal | null {
  if (!existsSync(transcriptPath)) return null;
  try {
    const bytes = statSync(transcriptPath).size;
    const tokens = bytes / 4;
    return { percent: clamp((tokens / windowTokens) * 100), estimate: true, source: "transcript" };
  } catch {
    return null;
  }
}

/** Bridge file first, transcript heuristic second, else a none-signal. */
export function readContextSignal(opts: {
  bridgePath?: string;
  transcriptPath?: string;
  windowTokens?: number;
}): ContextSignal {
  if (opts.bridgePath) {
    const bridge = readBridgeSignal(opts.bridgePath);
    if (bridge) return bridge;
  }
  if (opts.transcriptPath) {
    const heuristic = transcriptHeuristic(opts.transcriptPath, opts.windowTokens);
    if (heuristic) return heuristic;
  }
  return { percent: 0, estimate: true, source: "none" };
}

function clamp(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}
